import { useState, useEffect, useCallback, useMemo } from 'react';
import { IssueWithBuilding, IssuePriority } from '../../types/issues';
import { issuesApi } from '../../api/issuesApi';
import { formatDateOnlyForStorage } from '../../Helpers/DateUtils';

interface UseIssuesProps {
    buildingKey?: string;
}

interface UseIssuesFilters {
    priority?: IssuePriority | 'all';
}

interface UseIssuesReturn {
    // Data
    issues: IssueWithBuilding[];
    snoozedIssues: IssueWithBuilding[];  // Snoozed items (snoozedUntil > today)
    loading: boolean;
    error: string | null;
    submitting: boolean;

    // Filtered data
    filteredIssues: IssueWithBuilding[];

    // Filter state
    filters: UseIssuesFilters;
    setFilters: (filters: UseIssuesFilters) => void;

    // Actions
    archiveDirectly: (issueKey: string, reason?: string) => Promise<void>;
    snoozeIssue: (issueKey: string, snoozeDate: string) => Promise<void>;
    unsnoozeIssue: (issueKey: string) => Promise<void>;

    // Utilities
    refresh: () => Promise<void>;
    clearError: () => void;
}

export const useIssues = ({
    buildingKey
}: UseIssuesProps = {}): UseIssuesReturn => {
    const [issues, setIssues] = useState<IssueWithBuilding[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [filters, setFilters] = useState<UseIssuesFilters>({
        priority: 'all'
    });

    // Load issues
    const loadIssues = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            let data: IssueWithBuilding[];

            if (buildingKey) {
                // Get by building and enrich with building name
                const issuesList = await issuesApi.getByBuilding(buildingKey);
                data = issuesList.map(i => ({ ...i, buildingName: '' })) as IssueWithBuilding[];
            } else {
                data = await issuesApi.getAll();
            }

            setIssues(data);
        } catch (err) {
            console.error('Error loading issues:', err);
            setError('Kunde inte ladda felanmälningar');
        } finally {
            setLoading(false);
        }
    }, [buildingKey]);

    // Load on mount or when context changes
    useEffect(() => {
        loadIssues();
    }, [loadIssues]);

    // Separate active and snoozed issues (single-pass filtering)
    const { activeIssues, snoozedIssues } = useMemo(() => {
        const today = formatDateOnlyForStorage(new Date());

        const isSnoozed = (issue: IssueWithBuilding) =>
            issue.snoozedUntil && issue.snoozedUntil > today;

        const active: IssueWithBuilding[] = [];
        const snoozed: IssueWithBuilding[] = [];

        for (const issue of issues) {
            if (isSnoozed(issue)) {
                snoozed.push(issue);
            } else {
                active.push(issue);
            }
        }

        return { activeIssues: active, snoozedIssues: snoozed };
    }, [issues]);

    // Filter issues (only active, non-snoozed)
    const filteredIssues = useMemo(() => {
        let result = [...activeIssues];

        // Filter by priority
        if (filters.priority && filters.priority !== 'all') {
            result = result.filter(issue => issue.priority === filters.priority);
        }

        // Sort by priority (HIGH first) then createdAt (newest first)
        result.sort((a, b) => {
            const priorityOrder = { [IssuePriority.HIGH]: 0, [IssuePriority.MEDIUM]: 1, [IssuePriority.LOW]: 2 };
            const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
            if (priorityDiff !== 0) return priorityDiff;

            return b.createdAt.localeCompare(a.createdAt);
        });

        return result;
    }, [activeIssues, filters]);

    // Archive directly (without creating work order)
    const archiveDirectly = useCallback(async (
        issueKey: string,
        reason?: string
    ): Promise<void> => {
        setSubmitting(true);
        setError(null);

        try {
            await issuesApi.archiveDirectly(issueKey, reason);
            await loadIssues(); // Refresh list after archiving
        } catch (err: any) {
            console.error('Error archiving issue:', err);
            const message = err.message || 'Kunde inte arkivera felanmälan';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadIssues]);

    // Snooze issue
    const snoozeIssue = useCallback(async (
        issueKey: string,
        snoozeDate: string
    ): Promise<void> => {
        setSubmitting(true);
        setError(null);

        try {
            await issuesApi.snoozeIssue(issueKey, snoozeDate);
            await loadIssues(); // Refresh list after snoozing
        } catch (err: any) {
            console.error('Error snoozing issue:', err);
            const message = err.message || 'Kunde inte snooze felanmälan';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadIssues]);

    // Unsnooze issue
    const unsnoozeIssue = useCallback(async (
        issueKey: string
    ): Promise<void> => {
        setSubmitting(true);
        setError(null);

        try {
            await issuesApi.unsnoozeIssue(issueKey);
            await loadIssues(); // Refresh list after unsnoozing
        } catch (err: any) {
            console.error('Error unsnoozing issue:', err);
            const message = err.message || 'Kunde inte avsnooze felanmälan';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadIssues]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        issues: activeIssues,
        snoozedIssues,
        loading,
        error,
        submitting,
        filteredIssues,
        filters,
        setFilters,
        archiveDirectly,
        snoozeIssue,
        unsnoozeIssue,
        refresh: loadIssues,
        clearError
    };
};
